import type { Block } from 'blockly/core';

import { analogPinToCode } from '@skyrover/blockly-blocks';

import {
  arduinoGenerator,
  blockTraceComment,
  Order,
  quoteChar,
  quoteString,
} from './arduino-generator.js';

function statementPrefix(block: Block, code: string): string {
  return `${blockTraceComment(block)}\n${code}`;
}

// --- Arduino hardware blocks ---

arduinoGenerator.forBlock['arduino_delay'] = (block: Block) => {
  const ms = arduinoGenerator.valueToCode(block, 'MS', Order.NONE) || '0';
  return statementPrefix(block, `delay(${ms});\n`);
};

arduinoGenerator.forBlock['arduino_delay_microseconds'] = (block: Block) => {
  const us = arduinoGenerator.valueToCode(block, 'US', Order.NONE) || '0';
  return statementPrefix(block, `delayMicroseconds(${us});\n`);
};

arduinoGenerator.forBlock['arduino_millis'] = () => ['millis()', Order.FUNCTION_CALL];
arduinoGenerator.forBlock['arduino_micros'] = () => ['micros()', Order.FUNCTION_CALL];

arduinoGenerator.forBlock['arduino_pin_mode'] = (block: Block) => {
  const pin = block.getFieldValue('PIN');
  const mode = block.getFieldValue('MODE');
  return statementPrefix(block, `pinMode(${pin}, ${mode});\n`);
};

arduinoGenerator.forBlock['arduino_digital_write'] = (block: Block) => {
  const pin = block.getFieldValue('PIN');
  const state = block.getFieldValue('STATE');
  return statementPrefix(block, `digitalWrite(${pin}, ${state});\n`);
};

arduinoGenerator.forBlock['arduino_digital_read'] = (block: Block) => {
  const pin = block.getFieldValue('PIN');
  return [`digitalRead(${pin})`, Order.FUNCTION_CALL];
};

arduinoGenerator.forBlock['arduino_analog_read'] = (block: Block) => {
  const pin = analogPinToCode(String(block.getFieldValue('PIN')));
  return [`analogRead(${pin})`, Order.FUNCTION_CALL];
};

arduinoGenerator.forBlock['arduino_analog_write'] = (block: Block) => {
  const pin = block.getFieldValue('PIN');
  const value = arduinoGenerator.valueToCode(block, 'VALUE', Order.NONE) || '0';
  return statementPrefix(block, `analogWrite(${pin}, ${value});\n`);
};

arduinoGenerator.forBlock['arduino_serial_begin'] = (block: Block) => {
  const baud = block.getFieldValue('BAUD');
  arduinoGenerator.addSetup(`Serial.begin(${baud});`);
  return statementPrefix(block, `Serial.begin(${baud});\n`);
};

arduinoGenerator.forBlock['arduino_serial_print'] = (block: Block) => {
  const text = arduinoGenerator.valueToCode(block, 'TEXT', Order.NONE) || '""';
  return statementPrefix(block, `Serial.print(${text});\n`);
};

arduinoGenerator.forBlock['arduino_serial_println'] = (block: Block) => {
  const text = arduinoGenerator.valueToCode(block, 'TEXT', Order.NONE) || '""';
  return statementPrefix(block, `Serial.println(${text});\n`);
};

arduinoGenerator.forBlock['arduino_serial_available'] = () => ['Serial.available()', Order.FUNCTION_CALL];
arduinoGenerator.forBlock['arduino_serial_read'] = () => ['Serial.read()', Order.FUNCTION_CALL];
arduinoGenerator.forBlock['arduino_serial_read_char'] = () => [
  'static_cast<char>(Serial.read())',
  Order.FUNCTION_CALL,
];

arduinoGenerator.forBlock['arduino_high_low'] = (block: Block) => {
  return [String(block.getFieldValue('LEVEL')), Order.ATOMIC];
};

arduinoGenerator.forBlock['arduino_program'] = (block: Block) => {
  const setupBranch = arduinoGenerator.statementToCode(block, 'SETUP');
  const loopBranch = arduinoGenerator.statementToCode(block, 'LOOP');
  if (setupBranch.trim()) {
    arduinoGenerator.addSetup(setupBranch.trim());
  }
  if (loopBranch.trim()) {
    arduinoGenerator.addLoop(loopBranch.trim());
  }
  return '';
};

arduinoGenerator.forBlock['arduino_setup'] = (block: Block) => {
  const branch = arduinoGenerator.statementToCode(block, 'SETUP');
  if (branch.trim()) {
    arduinoGenerator.addSetup(branch.trim());
  }
  return '';
};

arduinoGenerator.forBlock['arduino_loop'] = (block: Block) => {
  const branch = arduinoGenerator.statementToCode(block, 'LOOP');
  if (branch.trim()) {
    arduinoGenerator.addLoop(branch.trim());
  }
  return '';
};

// --- Logic ---

arduinoGenerator.forBlock['controls_if'] = (block: Block) => {
  let code = '';
  let n = 0;
  do {
    const condition =
      arduinoGenerator.valueToCode(block, n === 0 ? 'IF0' : `IF${n}`, Order.NONE) || 'false';
    const branch = arduinoGenerator.statementToCode(block, n === 0 ? 'DO0' : `DO${n}`);
    code +=
      n === 0
        ? `if (${condition}) {\n${branch}}\n`
        : block.getFieldValue(`IS${n}`) === 'ELSEIF'
          ? `else if (${condition}) {\n${branch}}\n`
          : `else {\n${branch}}\n`;
    n++;
  } while (block.getInput(`IF${n}`));
  return code;
};

arduinoGenerator.forBlock['logic_compare'] = (block: Block) => {
  const operators: Record<string, string> = {
    EQ: '==',
    NEQ: '!=',
    LT: '<',
    LTE: '<=',
    GT: '>',
    GTE: '>=',
  };
  const op = operators[block.getFieldValue('OP')] ?? '==';
  const order = Order.RELATIONAL;
  const a = arduinoGenerator.valueToCode(block, 'A', order) || '0';
  const b = arduinoGenerator.valueToCode(block, 'B', order) || '0';
  return [`${a} ${op} ${b}`, order];
};

arduinoGenerator.forBlock['logic_operation'] = (block: Block) => {
  const op = block.getFieldValue('OP');
  const order = op === 'AND' ? Order.LOGICAL_AND : Order.LOGICAL_OR;
  const a = arduinoGenerator.valueToCode(block, 'A', order) || 'false';
  const b = arduinoGenerator.valueToCode(block, 'B', order) || 'false';
  return [`${a} ${op === 'AND' ? '&&' : '||'} ${b}`, order];
};

arduinoGenerator.forBlock['logic_negate'] = (block: Block) => {
  const arg = arduinoGenerator.valueToCode(block, 'BOOL', Order.LOGICAL_NOT) || 'true';
  return [`!${arg}`, Order.LOGICAL_NOT];
};

arduinoGenerator.forBlock['logic_boolean'] = (block: Block) => {
  const value = block.getFieldValue('BOOL') === 'TRUE' ? 'true' : 'false';
  return [value, Order.ATOMIC];
};

arduinoGenerator.forBlock['logic_null'] = () => ['NULL', Order.ATOMIC];

arduinoGenerator.forBlock['logic_ternary'] = (block: Block) => {
  const condition = arduinoGenerator.valueToCode(block, 'IF', Order.CONDITIONAL) || 'false';
  const thenVal = arduinoGenerator.valueToCode(block, 'THEN', Order.CONDITIONAL) || '0';
  const elseVal = arduinoGenerator.valueToCode(block, 'ELSE', Order.CONDITIONAL) || '0';
  return [`(${condition} ? ${thenVal} : ${elseVal})`, Order.CONDITIONAL];
};

// --- Loops ---

arduinoGenerator.forBlock['controls_repeat_ext'] = (block: Block) => {
  const repeats = arduinoGenerator.valueToCode(block, 'TIMES', Order.NONE) || '0';
  const branch = arduinoGenerator.statementToCode(block, 'DO');
  return `for (int i = 0; i < ${repeats}; i++) {\n${branch}}\n`;
};

arduinoGenerator.forBlock['controls_whileUntil'] = (block: Block) => {
  const until = block.getFieldValue('MODE') === 'UNTIL';
  const condition = arduinoGenerator.valueToCode(block, 'BOOL', Order.NONE) || 'false';
  const branch = arduinoGenerator.statementToCode(block, 'DO');
  return `while (${until ? '!' : ''}(${condition})) {\n${branch}}\n`;
};

arduinoGenerator.forBlock['controls_for'] = (block: Block) => {
  const variable = arduinoGenerator.getVariableName(block.getFieldValue('VAR'));
  const from = arduinoGenerator.valueToCode(block, 'FROM', Order.NONE) || '0';
  const to = arduinoGenerator.valueToCode(block, 'TO', Order.NONE) || '0';
  const by = arduinoGenerator.valueToCode(block, 'BY', Order.NONE) || '1';
  const branch = arduinoGenerator.statementToCode(block, 'DO');
  return `for (int ${variable} = ${from}; ${variable} <= ${to}; ${variable} += ${by}) {\n${branch}}\n`;
};

arduinoGenerator.forBlock['controls_flow_statements'] = (block: Block) => {
  const flow = block.getFieldValue('FLOW');
  return flow === 'BREAK' ? 'break;\n' : 'continue;\n';
};

// --- Math ---

arduinoGenerator.forBlock['math_number'] = (block: Block) => {
  return [String(Number(block.getFieldValue('NUM'))), Order.ATOMIC];
};

arduinoGenerator.forBlock['math_arithmetic'] = (block: Block) => {
  const operators: Record<string, [string, Order]> = {
    ADD: ['+', Order.ADDITIVE],
    MINUS: ['-', Order.ADDITIVE],
    MULTIPLY: ['*', Order.MULTIPLICATIVE],
    DIVIDE: ['/', Order.MULTIPLICATIVE],
    POWER: ['pow', Order.FUNCTION_CALL],
  };
  const tuple = operators[block.getFieldValue('OP')] ?? ['+', Order.ADDITIVE];
  const a = arduinoGenerator.valueToCode(block, 'A', tuple[1]) || '0';
  const b = arduinoGenerator.valueToCode(block, 'B', tuple[1]) || '0';
  if (tuple[0] === 'pow') {
    arduinoGenerator.addInclude('#include <math.h>');
    return [`pow(${a}, ${b})`, Order.FUNCTION_CALL];
  }
  return [`${a} ${tuple[0]} ${b}`, tuple[1]];
};

arduinoGenerator.forBlock['math_single'] = (block: Block) => {
  const op = block.getFieldValue('OP');
  const num = arduinoGenerator.valueToCode(block, 'NUM', Order.NONE) || '0';
  arduinoGenerator.addInclude('#include <math.h>');
  const ops: Record<string, string> = {
    ROOT: `sqrt(${num})`,
    ABS: `abs(${num})`,
    NEG: `-(${num})`,
    LN: `log(${num})`,
    LOG10: `log10(${num})`,
    EXP: `exp(${num})`,
    POW10: `pow(10, ${num})`,
  };
  return [ops[op] ?? num, Order.FUNCTION_CALL];
};

arduinoGenerator.forBlock['math_random_int'] = (block: Block) => {
  const from = arduinoGenerator.valueToCode(block, 'FROM', Order.NONE) || '0';
  const to = arduinoGenerator.valueToCode(block, 'TO', Order.NONE) || '0';
  return [`random(${from}, ${to} + 1)`, Order.FUNCTION_CALL];
};

arduinoGenerator.forBlock['math_constrain'] = (block: Block) => {
  const value = arduinoGenerator.valueToCode(block, 'VALUE', Order.NONE) || '0';
  const low = arduinoGenerator.valueToCode(block, 'LOW', Order.NONE) || '0';
  const high = arduinoGenerator.valueToCode(block, 'HIGH', Order.NONE) || '0';
  return [`constrain(${value}, ${low}, ${high})`, Order.FUNCTION_CALL];
};

arduinoGenerator.forBlock['math_modulo'] = (block: Block) => {
  const dividend = arduinoGenerator.valueToCode(block, 'DIVIDEND', Order.MULTIPLICATIVE) || '0';
  const divisor = arduinoGenerator.valueToCode(block, 'DIVISOR', Order.MULTIPLICATIVE) || '1';
  return [`(${dividend} % ${divisor})`, Order.MULTIPLICATIVE];
};

// --- Text ---

arduinoGenerator.forBlock['text'] = (block: Block) => {
  return [quoteString(String(block.getFieldValue('TEXT'))), Order.ATOMIC];
};

arduinoGenerator.forBlock['text_char'] = (block: Block) => {
  const raw = String(block.getFieldValue('CHAR') ?? 'A');
  const char = raw.trim().slice(0, 1) || 'A';
  return [quoteChar(char), Order.ATOMIC];
};

arduinoGenerator.forBlock['text_join'] = (block: Block) => {
  const parts: string[] = [];
  for (let i = 0; i < block.inputList.length; i++) {
    const input = block.inputList[i];
    if (input?.name?.startsWith('ADD')) {
      parts.push(arduinoGenerator.valueToCode(block, input.name, Order.NONE) || '""');
    }
  }
  if (parts.length === 0) {
    return ['""', Order.ATOMIC];
  }
  if (parts.length === 1) {
    return [parts[0]!, Order.ATOMIC];
  }
  return [`String(${parts.join(' + ')})`, Order.ADDITIVE];
};

arduinoGenerator.forBlock['text_length'] = (block: Block) => {
  const text = arduinoGenerator.valueToCode(block, 'VALUE', Order.NONE) || '""';
  return [`String(${text}).length()`, Order.FUNCTION_CALL];
};

// --- Lists (basic String array style) ---

arduinoGenerator.forBlock['lists_create_empty'] = () => ['{}', Order.ATOMIC];

arduinoGenerator.forBlock['lists_create_with'] = (block: Block) => {
  const elements: string[] = [];
  for (let i = 0; i < block.inputList.length; i++) {
    const input = block.inputList[i];
    if (input?.name?.startsWith('ADD')) {
      elements.push(arduinoGenerator.valueToCode(block, input.name, Order.NONE) || '0');
    }
  }
  return [`{${elements.join(', ')}}`, Order.ATOMIC];
};

arduinoGenerator.forBlock['lists_length'] = (block: Block) => {
  const list = arduinoGenerator.valueToCode(block, 'VALUE', Order.NONE) || '{}';
  return [`(sizeof(${list}) / sizeof(${list}[0]))`, Order.FUNCTION_CALL];
};

arduinoGenerator.forBlock['lists_getIndex'] = (block: Block) => {
  const list = arduinoGenerator.valueToCode(block, 'VALUE', Order.NONE) || '{}';
  const at = arduinoGenerator.valueToCode(block, 'AT', Order.NONE) || '0';
  return [`${list}[${at}]`, Order.ATOMIC];
};

// --- Variables ---

function generateVariablesGet(block: Block): [string, Order] {
  const name = arduinoGenerator.getVariableName(block.getFieldValue('VAR'));
  return [name, Order.ATOMIC];
}

function generateVariablesSet(block: Block): string {
  const name = arduinoGenerator.getVariableName(block.getFieldValue('VAR'));
  const value = arduinoGenerator.valueToCode(block, 'VALUE', Order.NONE) || '0';
  return statementPrefix(block, `${name} = ${value};\n`);
}

arduinoGenerator.forBlock['variables_get'] = generateVariablesGet;
arduinoGenerator.forBlock['variables_get_dynamic'] = generateVariablesGet;
arduinoGenerator.forBlock['variables_set'] = generateVariablesSet;
arduinoGenerator.forBlock['variables_set_dynamic'] = generateVariablesSet;

// --- Functions ---

function getProcedureArgs(block: Block): string[] {
  const args: string[] = [];
  for (let i = 0; i < block.inputList.length; i++) {
    const input = block.inputList[i];
    if (input?.name?.startsWith('ARG')) {
      args.push(arduinoGenerator.valueToCode(block, input.name, Order.NONE) || '0');
    }
  }
  return args;
}

arduinoGenerator.forBlock['procedures_defreturn'] = (block: Block) => {
  const funcName = String(block.getFieldValue('NAME')).replace(/\s+/g, '_');
  const branch = arduinoGenerator.statementToCode(block, 'STACK');
  const returnValue = arduinoGenerator.valueToCode(block, 'RETURN', Order.NONE) || '0';
  const argNames: string[] = (block as Block & { arguments_?: string[] }).arguments_ ?? [];
  const argList = argNames.map((a) => `float ${a}`).join(', ');
  arduinoGenerator.addGlobal(
    `float ${funcName}(${argList}) {\n${branch}  return ${returnValue};\n}\n`,
  );
  return '';
};

arduinoGenerator.forBlock['procedures_defnoreturn'] = (block: Block) => {
  const funcName = String(block.getFieldValue('NAME')).replace(/\s+/g, '_');
  const branch = arduinoGenerator.statementToCode(block, 'STACK');
  const argNames: string[] = (block as Block & { arguments_?: string[] }).arguments_ ?? [];
  const argList = argNames.map((a) => `float ${a}`).join(', ');
  arduinoGenerator.addGlobal(`void ${funcName}(${argList}) {\n${branch}}\n`);
  return '';
};

arduinoGenerator.forBlock['procedures_callreturn'] = (block: Block) => {
  const funcName = String(block.getFieldValue('NAME')).replace(/\s+/g, '_');
  const args = getProcedureArgs(block);
  return [`${funcName}(${args.join(', ')})`, Order.FUNCTION_CALL];
};

arduinoGenerator.forBlock['procedures_callnoreturn'] = (block: Block) => {
  const funcName = String(block.getFieldValue('NAME')).replace(/\s+/g, '_');
  const args = getProcedureArgs(block);
  return `${funcName}(${args.join(', ')});\n`;
};

export { arduinoGenerator };
